import { useState } from 'react';
import { ChevronDown, ChevronRight, AlertTriangle } from 'lucide-react';
import type { MaterialLibrary, FabricPackingResult } from '@estimator/shared';

/**
 * Minimal slot detail shape consumed by FinishSchedule.
 * Both BanquetteSlotDetail and BackCushionSlotDetail satisfy this.
 */
export interface FinishScheduleSlot {
  slot_id?: string;
  name: string;
  material_id?: string;
  material_name?: string;
  material_category?: string;
  material_subtype?: string | null;
  real_inches: number;
  sqft: number;
  cost: number;
  num_sheets?: number;
  sheet_size?: string;
  waste_factor_used?: number;
  fabric_pieces?: Array<{
    panel_type: 'front' | 'side' | 'back';
    section_index: number;
    panel_index?: number;
    width_in: number;
    length_in: number;
    design_up_axis: 'width' | 'length';
  }>;
  note?: string;
  currency_note?: string;
}

interface FinishScheduleProps {
  slotDetails: FinishScheduleSlot[];
  materials: MaterialLibrary[];
  /** One entry per fabric material across all slots. Empty / omitted when no fabric slots. */
  fabricPackings?: FabricPackingResult[];
}

interface GroupedMaterial {
  material_id: string;
  material_name: string;
  material_category: string;
  material_subtype: string | null;
  sheet_size?: string;
  total_sqft: number;
  total_cost: number;
  slots: FinishScheduleSlot[];
}

function fmt(n: number): string {
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

function pct(n: number): string {
  return `${(n * 100).toFixed(0)}%`;
}

export default function FinishSchedule({ slotDetails, materials, fabricPackings }: FinishScheduleProps) {
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  // Categories for fabric materials (by material_id) from slot details, since
  // fabric_packings don't carry category info directly.
  const fabricCategoryById = new Map<string, string>();
  const fabricSubtypeById = new Map<string, string | null>();
  for (const s of slotDetails) {
    if (s.material_id && s.material_subtype) {
      if (!fabricCategoryById.has(s.material_id)) {
        fabricCategoryById.set(s.material_id, s.material_category ?? 'Fabric');
        fabricSubtypeById.set(s.material_id, s.material_subtype);
      }
    }
  }
  // Fallback: pull category/subtype from the materials library if we have no
  // matching slot (rare, but possible if a packing has orphaned pieces).
  for (const p of fabricPackings ?? []) {
    if (!fabricCategoryById.has(p.material_id)) {
      const mat = materials.find((m) => m.id === p.material_id);
      fabricCategoryById.set(p.material_id, mat?.category ?? 'Fabric');
      fabricSubtypeById.set(p.material_id, (mat as any)?.fabric_subtype ?? null);
    }
  }

  // Split slots: fabric slots are rendered via fabric_packings (not via the
  // material grouping below). Non-fabric slots keep the sheet-goods path.
  const fabricMaterialIds = new Set((fabricPackings ?? []).map((p) => p.material_id));
  const nonFabricSlots = slotDetails.filter(
    (s) => s.material_id && !fabricMaterialIds.has(s.material_id) && !s.material_subtype,
  );

  // Group non-fabric slots by material_id
  const byMatId = new Map<string, GroupedMaterial>();
  for (const s of nonFabricSlots) {
    const key = s.material_id!;
    const existing = byMatId.get(key);
    if (existing) {
      existing.total_sqft += s.sqft;
      existing.total_cost += s.cost;
      existing.slots.push(s);
    } else {
      byMatId.set(key, {
        material_id: key,
        material_name: s.material_name ?? 'Unknown',
        material_category: s.material_category ?? 'Other',
        material_subtype: s.material_subtype ?? null,
        sheet_size: s.sheet_size,
        total_sqft: s.sqft,
        total_cost: s.cost,
        slots: [s],
      });
    }
  }

  // Category buckets: { category: { nonFabric: GroupedMaterial[]; fabric: FabricPackingResult[] } }
  const byCategory = new Map<string, { nonFabric: GroupedMaterial[]; fabric: FabricPackingResult[] }>();
  for (const m of byMatId.values()) {
    const cat = m.material_category || 'Other';
    const bucket = byCategory.get(cat) ?? { nonFabric: [], fabric: [] };
    bucket.nonFabric.push(m);
    byCategory.set(cat, bucket);
  }
  for (const p of fabricPackings ?? []) {
    const cat = fabricCategoryById.get(p.material_id) ?? 'Fabric';
    const bucket = byCategory.get(cat) ?? { nonFabric: [], fabric: [] };
    bucket.fabric.push(p);
    byCategory.set(cat, bucket);
  }

  if (byCategory.size === 0) {
    return (
      <div className="text-xs py-4 text-center" style={{ color: 'var(--text-muted)' }}>
        No finish materials assigned yet. Open Slot Manager to draw finish profiles.
      </div>
    );
  }

  const toggle = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const slotById = new Map<string, FinishScheduleSlot>();
  for (const s of slotDetails) {
    if (s.slot_id) slotById.set(s.slot_id, s);
  }

  return (
    <div className="flex flex-col gap-3">
      {Array.from(byCategory.entries()).map(([category, { nonFabric, fabric }]) => (
        <div key={category}>
          <div
            className="text-[10px] font-semibold uppercase tracking-wide mb-1.5"
            style={{ color: 'var(--text-muted)' }}
          >
            {category}
          </div>
          <div className="flex flex-col gap-1.5">
            {/* Non-fabric material cards */}
            {nonFabric.map((mat) => {
              const id = `m:${mat.material_id}`;
              const expanded = expandedIds.has(id);
              return (
                <div
                  key={id}
                  className="rounded-lg"
                  style={{ backgroundColor: 'var(--bg-panel)', border: '1px solid var(--border)' }}
                >
                  <button
                    type="button"
                    className="w-full flex items-center gap-2 px-3 py-2 text-left"
                    onClick={() => toggle(id)}
                  >
                    {expanded ? (
                      <ChevronDown size={14} style={{ color: 'var(--text-muted)' }} />
                    ) : (
                      <ChevronRight size={14} style={{ color: 'var(--text-muted)' }} />
                    )}
                    <span className="text-xs font-semibold flex-1" style={{ color: 'var(--text-primary)' }}>
                      {mat.material_name}
                    </span>
                    <span className="text-[11px]" style={{ color: 'var(--text-secondary)' }}>
                      {mat.total_sqft.toFixed(2)} sqft
                    </span>
                    <span className="text-xs font-semibold ml-2" style={{ color: '#e67e22' }}>
                      {fmt(mat.total_cost)}
                    </span>
                  </button>
                  {expanded && (
                    <div
                      className="px-3 pb-2.5 pt-1 text-[11px] space-y-2"
                      style={{ color: 'var(--text-secondary)', borderTop: '1px solid var(--border)' }}
                    >
                      {mat.sheet_size && (
                        <div style={{ color: 'var(--text-muted)' }}>
                          Sheet size: {mat.sheet_size}
                        </div>
                      )}
                      {mat.slots.map((s, i) => (
                        <div key={i} className="pl-2 border-l-2" style={{ borderColor: 'var(--border)' }}>
                          <div className="font-medium" style={{ color: 'var(--text-primary)' }}>
                            {s.name}
                          </div>
                          {s.note && (
                            <div style={{ color: 'var(--text-muted)' }}>({s.note})</div>
                          )}
                          <div>
                            Profile: {s.real_inches.toFixed(1)}"
                          </div>
                          <div>
                            Raw: {s.sqft.toFixed(2)} sqft{s.waste_factor_used != null && ` (waste +${pct(s.waste_factor_used)} applied)`}
                          </div>
                          <div>
                            {s.num_sheets != null && `${s.num_sheets} sheet${s.num_sheets !== 1 ? 's' : ''} -> `}{fmt(s.cost)}
                          </div>
                          {s.currency_note && (
                            <div className="text-[10px]" style={{ color: 'var(--text-muted)' }}>
                              {s.currency_note}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}

            {/* Fabric packing cards */}
            {fabric.map((p) => {
              const id = `f:${p.material_id}`;
              const expanded = expandedIds.has(id);
              const subtype = fabricSubtypeById.get(p.material_id);
              const rollYards = p.rolls.map((r) => r.length_consumed_in / 36);
              const rollYardsLabel = rollYards.length > 0
                ? `${p.total_rolls} roll${p.total_rolls !== 1 ? 's' : ''}: ${rollYards.map((y) => y.toFixed(1)).join(' + ')} yd`
                : 'No rolls used';
              return (
                <div
                  key={id}
                  className="rounded-lg"
                  style={{ backgroundColor: 'var(--bg-panel)', border: '1px solid var(--border)' }}
                >
                  <button
                    type="button"
                    className="w-full flex items-center gap-2 px-3 py-2 text-left"
                    onClick={() => toggle(id)}
                  >
                    {expanded ? (
                      <ChevronDown size={14} style={{ color: 'var(--text-muted)' }} />
                    ) : (
                      <ChevronRight size={14} style={{ color: 'var(--text-muted)' }} />
                    )}
                    <span className="text-xs font-semibold flex-1" style={{ color: 'var(--text-primary)' }}>
                      {p.material_name}
                      {subtype && (
                        <span className="ml-1 text-[10px]" style={{ color: 'var(--text-muted)' }}>
                          ({subtype})
                        </span>
                      )}
                    </span>
                    {p.warnings && p.warnings.length > 0 && (
                      <AlertTriangle size={12} style={{ color: '#e67e22' }} />
                    )}
                    <span className="text-[11px]" style={{ color: 'var(--text-secondary)' }}>
                      {p.final_yards.toFixed(2)} yd
                    </span>
                    <span className="text-xs font-semibold ml-2" style={{ color: '#e67e22' }}>
                      {fmt(p.cost)}
                    </span>
                  </button>
                  {expanded && (
                    <div
                      className="px-3 pb-2.5 pt-1 text-[11px] space-y-2"
                      style={{ color: 'var(--text-secondary)', borderTop: '1px solid var(--border)' }}
                    >
                      <div style={{ color: 'var(--text-muted)' }}>
                        Roll width: {p.roll_width_in}" · Pattern: {p.pattern_direction}
                      </div>
                      <div>{rollYardsLabel}</div>
                      <div style={{ color: 'var(--text-muted)' }}>
                        Packed: {(p.total_length_in / 36).toFixed(2)} yd
                        {' '}× (1 + Design {pct(p.design_pct)} + Pattern {pct(p.pattern_allowance_pct)} + Mat waste {pct(p.material_waste_factor)})
                        {' = '}{p.final_yards.toFixed(2)} yd
                      </div>
                      {p.currency_note && (
                        <div className="text-[10px]" style={{ color: 'var(--text-muted)' }}>
                          {p.currency_note}
                        </div>
                      )}
                      {p.warnings && p.warnings.map((w, i) => (
                        <div key={`w-${i}`} className="text-[10px] flex items-start gap-1" style={{ color: '#e67e22' }}>
                          <AlertTriangle size={10} className="mt-[2px] flex-shrink-0" />
                          <span>{w}</span>
                        </div>
                      ))}

                      {/* Per-slot piece breakdown */}
                      {p.contributing_slot_ids.length > 0 && (
                        <div className="space-y-1.5 pt-1">
                          <div className="text-[10px] uppercase" style={{ color: 'var(--text-muted)' }}>
                            Cut pieces by slot
                          </div>
                          {p.contributing_slot_ids.map((sid) => {
                            const slot = slotById.get(sid);
                            const pieces = slot?.fabric_pieces ?? [];
                            return (
                              <div
                                key={sid}
                                className="pl-2 border-l-2"
                                style={{ borderColor: 'var(--border)' }}
                              >
                                <div className="font-medium" style={{ color: 'var(--text-primary)' }}>
                                  {slot?.name ?? `Slot ${sid.slice(0, 8)}…`}
                                  {slot?.cost != null && (
                                    <span className="ml-2 font-normal" style={{ color: 'var(--text-muted)' }}>
                                      {fmt(slot.cost)}
                                    </span>
                                  )}
                                </div>
                                {pieces.length === 0 ? (
                                  <div style={{ color: 'var(--text-muted)' }}>No pieces</div>
                                ) : (
                                  pieces.map((pc, i) => (
                                    <div key={i} style={{ color: 'var(--text-muted)' }}>
                                      {pc.panel_type}
                                      {pc.panel_type === 'side' && pc.panel_index != null
                                        ? ` #${pc.panel_index + 1}`
                                        : ''}{' '}
                                      (§{pc.section_index + 1}): {pc.width_in.toFixed(1)}" × {pc.length_in.toFixed(1)}"
                                    </div>
                                  ))
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
